import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { CustomerSortFields, SortDirection, GetCustomersQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/customer';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useCustomersTable = ({ queryOptions }: { queryOptions?: GetCustomersQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetCustomersQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as CustomerSortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withProjects: true,
                  withTenant: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: customerData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetCustomersQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      customerData
        ? { totalCount: customerData.customers.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [customerData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type CustomersBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function CustomersBaseTable(props: CustomersBaseTableProps) {
  const tableProps = useCustomersTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
